import React, { useState, useEffect, useRef } from 'react';
import './MobileMoneyGateway.css';
import {
  initiateOnlineCheckout,
  initiatePreapproval,
  verifyPreapprovalOTP,
  chargeCustomer,
  generateReference,
  formatPhoneToHubtel,
  getChannelFromCarrier
} from '../utils/hubtel';
import mtnLogo from '../assets/mtn-logo.png';
import vodafoneLogo from '../assets/vodafone-logo.png';
import airteltigoLogo from '../assets/airteltigo-logo.png';
import PaymentTerminal from './PaymentTerminal';

const MobileMoneyGateway = ({ isOpen, onClose, planData, onSuccess, initialData = {}, onHubtelRedirect }) => {
  // Gateway type selection: 'selector' | 'online-checkout' | 'direct-debit'
  const [view, setView] = useState('selector');
  
  // Common state
  const [carrier, setCarrier] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  
  // Direct Debit state
  const [preapprovalId, setPreapprovalId] = useState('');
  const [otpPrefix, setOtpPrefix] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [directDebitStep, setDirectDebitStep] = useState(1); // 1: initiate, 2: otp, 3: processing
  
  // Online Checkout state
  const [checkoutUrl, setCheckoutUrl] = useState('');
  
  // Terminal state
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalData, setTerminalData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'checkout' or 'direct-debit'

  const carriers = [
    { 
      id: 'mtn', 
      name: 'MTN Mobile Money', 
      code: 'MTN', 
      color: '#ffcc00',
      bgColor: '#ffcc00',
      textColor: '#000000',
      logo: mtnLogo
    },
    { 
      id: 'vodafone', 
      name: 'Vodafone Cash', 
      code: 'VOD', 
      color: '#e60000',
      bgColor: '#ffffff',
      textColor: '#e60000',
      logo: vodafoneLogo
    },
    { 
      id: 'airteltigo', 
      name: 'AirtelTigo Money', 
      code: 'ATL', 
      color: '#ff6600',
      bgColor: '#ffffff',
      textColor: '#ff6600',
      logo: airteltigoLogo
    }
  ];

  // Track if this is the first time the modal opens
  const isFirstOpen = useRef(true);
  
  useEffect(() => {
    if (isOpen && isFirstOpen.current) {
      isFirstOpen.current = false;
      // Reset all state when modal first opens
      setView('selector');
      setCarrier(initialData.carrier || '');
      setPhoneNumber(initialData.phone || '');
      setEmail(initialData.email || '');
      setLoading(false);
      setError('');
      setReference('');
      setPreapprovalId('');
      setOtpPrefix('');
      setOtpCode('');
      setDirectDebitStep(1);
      setCheckoutUrl('');
    }
    
    // Reset the ref when modal closes
    if (!isOpen) {
      isFirstOpen.current = true;
    }
  }, [isOpen]); // Only depend on isOpen, not initialData

  // Generate return/cancel URLs based on current URL
  const getBaseUrl = () => {
    return window.location.origin;
  };

  // ============ ONLINE CHECKOUT HANDLER ============
  const handleOnlineCheckout = async () => {
    // Validation
    if (!carrier) {
      setError('Please select your mobile network (MTN, Vodafone, or AirtelTigo)');
      return;
    }
    if (!phoneNumber || phoneNumber.length < 9) {
      setError('Please enter a valid phone number (9 digits without the leading 0)');
      return;
    }

    // Generate reference for terminal display
    const clientRef = generateReference('MMO');
    setReference(clientRef);

    // Show terminal first
    setTerminalData({
      carrier: carrier,
      phoneNumber: phoneNumber,
      amount: planData?.price || 0,
      reference: clientRef
    });
    setPendingAction('checkout');
    setShowTerminal(true);
  };

  // Process online checkout after terminal completes
  const processOnlineCheckout = async () => {
    setLoading(true);
    setError('');

    const baseUrl = getBaseUrl();
    const callbackUrl = `${baseUrl}/api/hubtel/callback`;
    const returnUrl = `${baseUrl}/payment/success?ref=${reference}&method=hubtel-checkout`;
    const cancellationUrl = `${baseUrl}/payment/cancel?ref=${reference}`;

    try {
      const result = await initiateOnlineCheckout({
        amount: planData?.price || 0,
        description: `Payment for ${planData?.name || 'Plan'} - ${reference}`,
        clientReference: reference,
        callbackUrl,
        returnUrl,
        cancellationUrl
      });

      if (result.success && result.checkoutUrl) {
        setCheckoutUrl(result.checkoutUrl);
        
        // Update session storage with reference for tracking
        const session = JSON.parse(sessionStorage.getItem('paymentSession') || '{}');
        session.hubtelReference = reference;
        session.checkoutUrl = result.checkoutUrl;
        session.status = 'redirecting';
        sessionStorage.setItem('paymentSession', JSON.stringify(session));
        
        // Close modals before redirect
        onClose(); // Close gateway modal
        if (onHubtelRedirect) {
          onHubtelRedirect(); // Close parent payment modal
        }
        
        // Small delay to allow modals to close before redirect
        setTimeout(() => {
          window.location.href = result.checkoutUrl;
        }, 300);
      } else {
        setError(result.error || 'Failed to initiate checkout');
        setLoading(false);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  // ============ DIRECT DEBIT HANDLERS ============
  const handleInitiateDirectDebit = async () => {
    // Validation
    if (!carrier) {
      setError('Please select your mobile network (MTN or Vodafone)');
      return;
    }
    if (!phoneNumber || phoneNumber.length < 9) {
      setError('Please enter a valid phone number (9 digits without the leading 0)');
      return;
    }

    // Generate reference for terminal display
    const clientRef = generateReference('MMD');
    setReference(clientRef);

    // Show terminal first
    setTerminalData({
      carrier: carrier,
      phoneNumber: phoneNumber,
      amount: planData?.price || 0,
      reference: clientRef
    });
    setPendingAction('direct-debit');
    setShowTerminal(true);
  };

  // Process direct debit after terminal completes
  const processDirectDebit = async () => {
    setLoading(true);
    setError('');

    const formattedPhone = formatPhoneToHubtel(phoneNumber);
    const channel = getChannelFromCarrier(carrier, true);
    const baseUrl = getBaseUrl();
    const callbackUrl = `${baseUrl}/api/hubtel/callback`;

    try {
      const result = await initiatePreapproval({
        customerMsisdn: formattedPhone,
        channel,
        callbackUrl,
        clientReferenceId: reference
      });

      if (result.success) {
        setPreapprovalId(result.hubtelPreApprovalId);
        setOtpPrefix(result.otpPrefix);
        setDirectDebitStep(2); // Move to OTP step
      } else {
        setError(result.error || 'Failed to initiate direct debit');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle terminal completion
  const handleTerminalComplete = () => {
    setShowTerminal(false);
    
    // Execute the pending action
    if (pendingAction === 'checkout') {
      processOnlineCheckout();
    } else if (pendingAction === 'direct-debit') {
      processDirectDebit();
    }
    
    setPendingAction(null);
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length < 4) {
      setError('Please enter the complete OTP');
      return;
    }

    setLoading(true);
    setError('');

    const formattedPhone = formatPhoneToHubtel(phoneNumber);

    try {
      const result = await verifyPreapprovalOTP({
        customerMsisdn: formattedPhone,
        hubtelPreApprovalId: preapprovalId,
        clientReferenceId: reference,
        otpCode: otpCode.trim()
      });

      if (result.success) {
        setDirectDebitStep(3);
        // Automatically proceed to charge
        handleChargeCustomer();
      } else {
        setError(result.error || 'OTP verification failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleChargeCustomer = async () => {
    const chargeRef = generateReference('MMCHARGE');
    const formattedPhone = formatPhoneToHubtel(phoneNumber);
    const channel = getChannelFromCarrier(carrier, true);
    const baseUrl = getBaseUrl();
    const callbackUrl = `${baseUrl}/api/hubtel/callback`;

    try {
      const result = await chargeCustomer({
        customerMsisdn: formattedPhone,
        channel,
        amount: planData?.price || 0,
        description: `Direct Debit - ${planData?.name || 'Plan'}`,
        callbackUrl,
        clientReference: chargeRef,
        customerName: email ? email.split('@')[0] : 'Customer',
        customerEmail: email
      });

      if (result.success) {
        // Store payment info and show success
        onSuccess({
          reference: chargeRef,
          method: 'hubtel-direct-debit',
          amount: planData?.price,
          plan: planData?.name,
          phone: formattedPhone
        });
      } else {
        setError(result.error || 'Charge failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Network error during charge');
      setLoading(false);
    }
  };

  if (!isOpen || !planData) return null;

  return (
    <>
      {/* Payment Terminal */}
      <PaymentTerminal 
        isOpen={showTerminal}
        onComplete={handleTerminalComplete}
        paymentData={terminalData}
      />
      
      <div className="mm-gateway-overlay" onClick={onClose}>
        <div className="mm-gateway-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="mm-gateway-header">
          <h3><i className="fas fa-mobile-alt"></i> Mobile Money Payment</h3>
          <button className="mm-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Amount Display */}
        <div className="mm-amount-banner">
          <span className="mm-amount-label">Amount to Pay</span>
          <span className="mm-amount-value">GH₵{planData.price}</span>
          <span className="mm-plan-name">{planData.name} Plan</span>
        </div>

        <div className="mm-gateway-body">
          {/* VIEW: SELECTOR */}
          {view === 'selector' && (
            <div className="mm-selector-view">
              <h4 className="mm-section-title">Select Payment Method</h4>
              
              <div className="mm-options">
                {/* Online Checkout Option */}
                <div 
                  className="mm-option-card"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setView('online-checkout');
                  }}
                >
                  <div className="mm-option-icon" style={{ background: 'linear-gradient(135deg, #2d9cdb, #1c7ab3)' }}>
                    <i className="fas fa-external-link-alt"></i>
                  </div>
                  <div className="mm-option-content">
                    <h5>Online Checkout</h5>
                    <p>Pay securely on Hubtel's website. You'll be redirected to complete payment.</p>
                    <span className="mm-option-badge">Recommended</span>
                  </div>
                  <i className="fas fa-chevron-right mm-option-arrow"></i>
                </div>

                {/* Direct Debit Option */}
                <div 
                  className="mm-option-card"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setView('direct-debit');
                  }}
                >
                  <div className="mm-option-icon" style={{ background: 'linear-gradient(135deg, #00c853, #009624)' }}>
                    <i className="fas fa-bolt"></i>
                  </div>
                  <div className="mm-option-content">
                    <h5>Direct Debit</h5>
                    <p>Quick payment using pre-approved mobile money. Requires OTP verification.</p>
                    <span className="mm-option-badge secondary">Fast</span>
                  </div>
                  <i className="fas fa-chevron-right mm-option-arrow"></i>
                </div>
              </div>

              <div className="mm-security-note">
                <i className="fas fa-shield-alt"></i>
                <span>Powered by Hubtel - Secure & Reliable</span>
              </div>
            </div>
          )}

          {/* VIEW: ONLINE CHECKOUT */}
          {view === 'online-checkout' && (
            <div className="mm-checkout-view">
              <button className="mm-back-btn" onClick={() => setView('selector')}>
                <i className="fas fa-arrow-left"></i> Back
              </button>

              <h4 className="mm-section-title">Online Checkout</h4>

              {/* Carrier Selection */}
              <div className="mm-carrier-section">
                <label>Select Network</label>
                <div className="mm-carrier-grid">
                  {carriers.map((c) => (
                    <div
                      key={c.id}
                      className={`mm-carrier-card ${carrier === c.id ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarrier(c.id);
                      }}
                      style={{ '--carrier-color': c.color }}
                    >
                      <div 
                        className="mm-carrier-logo"
                        style={{ 
                          background: '#ffffff',
                          border: `2px solid ${c.color}`,
                          padding: '2px',
                          overflow: 'hidden'
                        }}
                      >
                        <img 
                          src={c.logo} 
                          alt={c.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '50%'
                          }}
                        />
                      </div>
                      <span>{c.name}</span>
                      {carrier === c.id && <i className="fas fa-check-circle mm-check"></i>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Phone Number - Required */}
              <div className="mm-input-group">
                <label>
                  <i className="fas fa-mobile-alt" style={{marginRight: '6px', color: 'var(--neon-blue)'}}></i>
                  Phone Number <span className="required">*</span>
                  {phoneNumber.length >= 9 && (
                    <i className="fas fa-check-circle" style={{marginLeft: '8px', color: 'var(--neon-green)', fontSize: '0.9rem'}}></i>
                  )}
                </label>
                <div className={`mm-phone-input ${phoneNumber.length >= 9 ? 'valid' : ''}`}>
                  <span className="mm-prefix">+233</span>
                  <input
                    type="tel"
                    placeholder="24 XXX XXXX"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 9));
                      if (error) setError(''); // Clear error on type
                    }}
                    autoFocus
                  />
                  {phoneNumber.length >= 9 && (
                    <i className="fas fa-check" style={{color: 'var(--neon-green)', marginRight: '12px'}}></i>
                  )}
                </div>
                <span className="mm-hint">
                  <i className="fas fa-info-circle" style={{marginRight: '4px'}}></i>
                  Enter number without leading 0 (e.g., 241234567)
                </span>
              </div>

              {/* Email - Optional */}
              <div className="mm-input-group">
                <label>
                  <i className="fas fa-envelope" style={{marginRight: '6px', color: 'var(--text-secondary)'}}></i>
                  Email Address <span className="optional">(optional)</span>
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <span className="mm-hint">For payment receipt (recommended)</span>
              </div>

              {error && <div className="mm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

              <button 
                className="mm-pay-btn"
                onClick={handleOnlineCheckout}
                disabled={loading || !carrier || phoneNumber.length < 9}
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  <><i className="fas fa-external-link-alt"></i> Continue to Payment</>
                )}
              </button>

              <p className="mm-redirect-note">
                <i className="fas fa-info-circle"></i>
                You will be redirected to Hubtel's secure payment page
              </p>
            </div>
          )}

          {/* VIEW: DIRECT DEBIT */}
          {view === 'direct-debit' && (
            <div className="mm-direct-debit-view">
              <button className="mm-back-btn" onClick={() => setView('selector')}>
                <i className="fas fa-arrow-left"></i> Back
              </button>

              <h4 className="mm-section-title">Direct Debit</h4>

              {/* Step 1: Initiate */}
              {directDebitStep === 1 && (
                <>
                  {/* Carrier Selection */}
                  <div className="mm-carrier-section">
                    <label>Select Network</label>
                    <div className="mm-carrier-grid">
                      {carriers.filter(c => c.id !== 'airteltigo').map((c) => (
                        <div
                          key={c.id}
                          className={`mm-carrier-card ${carrier === c.id ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCarrier(c.id);
                          }}
                          style={{ '--carrier-color': c.color }}
                        >
                          <div 
                            className="mm-carrier-logo"
                            style={{ 
                              background: c.bgColor,
                              color: c.textColor,
                              border: c.id === 'vodafone' ? `2px solid ${c.color}` : 'none'
                            }}
                          >
                            <span className="carrier-code">{c.code}</span>
                          </div>
                          <span>{c.name}</span>
                          {carrier === c.id && <i className="fas fa-check-circle mm-check"></i>}
                        </div>
                      ))}
                    </div>
                    <span className="mm-hint">* AirtelTigo direct debit coming soon</span>
                  </div>

                  {/* Phone Number - Required */}
                  <div className="mm-input-group">
                    <label>
                      <i className="fas fa-mobile-alt" style={{marginRight: '6px', color: 'var(--neon-blue)'}}></i>
                      Phone Number <span className="required">*</span>
                      {phoneNumber.length >= 9 && (
                        <i className="fas fa-check-circle" style={{marginLeft: '8px', color: 'var(--neon-green)', fontSize: '0.9rem'}}></i>
                      )}
                    </label>
                    <div className={`mm-phone-input ${phoneNumber.length >= 9 ? 'valid' : ''}`}>
                      <span className="mm-prefix">+233</span>
                      <input
                        type="tel"
                        placeholder="24 XXX XXXX"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 9));
                          if (error) setError(''); // Clear error on type
                        }}
                        autoFocus
                      />
                      {phoneNumber.length >= 9 && (
                        <i className="fas fa-check" style={{color: 'var(--neon-green)', marginRight: '12px'}}></i>
                      )}
                    </div>
                    <span className="mm-hint">
                      <i className="fas fa-info-circle" style={{marginRight: '4px'}}></i>
                      Enter number without leading 0
                    </span>
                  </div>

                  {error && <div className="mm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

                  <button 
                    className="mm-pay-btn"
                    onClick={handleInitiateDirectDebit}
                    disabled={loading || !carrier || phoneNumber.length < 9}
                  >
                    {loading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Sending OTP...</>
                    ) : (
                      <><i className="fas fa-paper-plane"></i> Continue</>
                    )}
                  </button>
                </>
              )}

              {/* Step 2: OTP Verification */}
              {directDebitStep === 2 && (
                <div className="mm-otp-section">
                  <div className="mm-otp-info">
                    <i className="fas fa-mobile-alt"></i>
                    <p>Enter the OTP sent to your phone</p>
                    {otpPrefix && (
                      <div className="mm-otp-prefix">
                        <span>OTP Prefix: <strong>{otpPrefix}</strong></span>
                      </div>
                    )}
                  </div>

                  <div className="mm-input-group">
                    <label>Enter Full OTP (e.g., {otpPrefix || 'XXXX'}-1234)</label>
                    <input
                      type="text"
                      placeholder="XXXX-1234"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.toUpperCase())}
                      className="mm-otp-input"
                    />
                  </div>

                  {error && <div className="mm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

                  <button 
                    className="mm-pay-btn"
                    onClick={handleVerifyOTP}
                    disabled={loading || otpCode.length < 4}
                  >
                    {loading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Verifying...</>
                    ) : (
                      <><i className="fas fa-check"></i> Verify OTP</>
                    )}
                  </button>

                  <button 
                    className="mm-resend-btn"
                    onClick={() => setDirectDebitStep(1)}
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              {/* Step 3: Processing */}
              {directDebitStep === 3 && (
                <div className="mm-processing">
                  <div className="mm-spinner-large"></div>
                  <h5>Processing Payment</h5>
                  <p>Please wait while we charge your mobile money...</p>
                  <div className="mm-ref-display">
                    <span>Ref: {reference}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default MobileMoneyGateway;
